import React, { useState, useEffect } from 'react';
import {
  Typography,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  Checkbox,
  FormHelperText,
  Grid,
  Box,
  Breadcrumbs,
  Link,
  Alert,
  AlertTitle,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, NetworkCheck, NavigateNext } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useUserRights } from '../../context/UserRightsContext';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100vh - 120px)',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  margin: theme.spacing(1),
}));

const HeaderContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  // borderBottom: `1px solid ${theme.palette.divider}`,
}));

const TitleBar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 2),
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
}));

const ButtonGroup = styled('div')({
  display: 'flex',
  gap: 8,
});

const Title = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontWeight: 600,
  '& svg': {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
}));

const BreadcrumbContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(0.5, 2),
  backgroundColor: theme.palette.background.default,
  borderBottom: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(2), // Add space below breadcrumbs
}));

const FormControlStyled = styled(FormControl)(({ theme }) => ({
  minWidth: 120,
}));

const RootDiv = styled('div')({
  flexGrow: 1,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const ContentDiv = styled('div')({
  flexGrow: 1,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  padding: '0',
});

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  border: 'none',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#F8F8F8', // Light gray background for headers
    color: '#6B0F2A', // Maroon text on light background
    fontWeight: 600,
    fontSize: '0.875rem', // 14px for headers
  },
  '& .MuiDataGrid-cell': {
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: '0.8125rem', // 13px for row data (slightly smaller than headers)
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'rgba(139, 159, 232, 0.08)',
  },
  '& .MuiDataGrid-row.Mui-selected': {
    backgroundColor: 'rgba(139, 159, 232, 0.16)',
    '&:hover': {
      backgroundColor: 'rgba(139, 159, 232, 0.24)',
    },
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottomLeftRadius: theme.shape.borderRadius * 2,
    borderBottomRightRadius: theme.shape.borderRadius * 2,
  },
  '& .MuiTablePagination-root': {
    color: theme.palette.text.secondary,
  },
}));

// Breadcrumb Component
const DynamicBreadcrumbs = ({ paths = [] }) => {
  return (
    <BreadcrumbContainer>
      <Breadcrumbs separator={<NavigateNext fontSize="small" />} aria-label="breadcrumb">
        {paths.map((path, index) => {
          const isLast = index === paths.length - 1;
          return isLast ? (
            <Typography
              key={path.label}
              sx={{ display: 'flex', alignItems: 'center' }}
              color="text.primary"
            >
              {path.icon && React.createElement(path.icon, { sx: { mr: 0.5 }, fontSize: 'inherit' })}
              {path.label}
            </Typography>
          ) : (
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center' }}
              color="inherit"
              href={path.href || '#'}
              key={path.label}
            >
              {path.icon && React.createElement(path.icon, { sx: { mr: 0.5 }, fontSize: 'inherit' })}
              {path.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    </BreadcrumbContainer>
  );
};

// Dynamic DataGrid Component
const DynamicDataGrid = ({
  rows,
  columns,
  selectedRow,
  onRowClick,
  onRowDoubleClick,
  pageSize = 50,
  paginationModel,
  onPaginationModelChange,
  rowHeight = 55,
  headerHeight = 48,
  densePadding = false,
  sx: gridSx,
}) => {
  const controlledPagination = paginationModel != null && typeof onPaginationModelChange === 'function';
  return (
    <div
      style={{
        flexGrow: 1,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        paddingLeft: densePadding ? 0 : 16,
        paddingRight: densePadding ? 0 : 16,
      }}
    >
      <StyledDataGrid
        rows={rows}
        columns={columns}
        pageSizeOptions={[25, 50, 100]}
        {...(controlledPagination
          ? {
              paginationModel,
              onPaginationModelChange,
            }
          : {
              initialState: {
                pagination: {
                  paginationModel: {
                    pageSize: pageSize,
                  },
                },
              },
            })}
        onRowClick={onRowClick}
        onRowDoubleClick={onRowDoubleClick}
        disableRowSelectionOnClick
        density="compact"
        autoHeight={false}
        headerHeight={headerHeight}
        rowHeight={rowHeight}
        showCellRightBorder={false}
        showColumnRightBorder={false}
        getRowClassName={(params) => (params.id === selectedRow ? 'Mui-selected' : '')}
        sx={{ height: '100%', ...gridSx }}
      />
    </div>
  );
};

// Delete Confirmation Dialog Component
const DeleteConfirmationDialog = ({ open, onClose, onConfirm, title = "Confirm Delete", message = "Are you sure you want to delete this item? This action cannot be undone." }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="delete-dialog-title"
      aria-describedby="delete-dialog-description"
    >
      <DialogTitle id="delete-dialog-title">
        {title}
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mt: 1 }}>
          <AlertTitle>Warning</AlertTitle>
          {message}
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button onClick={onConfirm} color="error" variant="contained" autoFocus>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const parseFieldErrorsFromMessage = (message) => {
  if (!message) return {};
  const text = String(message);
  const requiredMatch = text.match(/^([\w.]+)\s+is required/i);
  if (requiredMatch) {
    return { [requiredMatch[1]]: text };
  }
  const jsonMatch = text.match(/^([\w.]+):\s*invalid JSON/i);
  if (jsonMatch) {
    return { [jsonMatch[1]]: text };
  }
  return {};
};

// Dynamic Form Dialog Component
const DynamicFormDialog = ({
  open,
  onClose,
  title,
  formData,
  onChange,
  onSubmit,
  fields,
  editMode,
  fieldErrors = {},
  submitError = '',
}) => {
  // Note: Field-specific onChange handlers (onCategoryChange, etc.) are called from handleInputChange
  // in DynamicContainer, not here, to avoid duplicate calls

  const renderField = (field) => {
            if (field.hideOnEdit && editMode) return null;
            if (field.hideOnCreate && !editMode) return null;
            if (field.show && !field.show(formData)) return null;

    if (field.type === 'select') {
      const isRequired = typeof field.required === 'function' ? field.required(formData) : field.required;
      const validateError = field.validate && field.validate(formData[field.name], formData);
      const fieldError = fieldErrors[field.name] || validateError;
      return (
        <Grid item xs={12} sm={field.width || 6} key={field.name}>
          <FormControlStyled 
            fullWidth 
            size="small"
            required={isRequired}
            error={Boolean(fieldError)}
          >
            <InputLabel>{field.label}</InputLabel>
            <Select
              name={field.name}
              value={formData[field.name] || ''}
              onChange={(e) => onChange(e, field)}
              label={field.label}
              variant="outlined"
              disabled={field.disabled || (field.disableOnEdit && editMode)}
            >
              {field.options.map((option) => {
                const value = typeof option === 'object' ? option.value : option;
                const label = typeof option === 'object' ? option.label : option;
                const isDisabled = typeof option === 'object' ? option.disabled : false;
                return (
                  <MenuItem 
                    key={value} 
                    value={value}
                    disabled={isDisabled}
                    sx={{
                      opacity: isDisabled ? 0.5 : 1,
                      fontStyle: isDisabled ? 'italic' : 'normal',
                    }}
                  >
                    {label}
                  </MenuItem>
                );
              })}
            </Select>
            {(fieldError || field.helperText) && (
              <FormHelperText error={Boolean(fieldError)}>
                {fieldError || field.helperText}
              </FormHelperText>
            )}
          </FormControlStyled>
        </Grid>
      );
    } else if (field.type === 'custom') {
      return (
        <Grid item xs={12} sm={field.width || 6} key={field.name}>
          <field.component
            value={formData[field.name] || {}}
            formData={formData}
            onChange={(value) => onChange({ target: { name: field.name, value } }, field)}
            error={field.validate && field.validate(formData[field.name], formData)}
            {...(field.props || {})}
          />
        </Grid>
      );
    } else if (field.type === 'checkbox') {
      return (
        <Grid item xs={12} sm={field.width || 6} key={field.name} sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <FormControlLabel
            control={
              <Checkbox
                name={field.name}
                checked={Boolean(formData[field.name])}
                onChange={(e) => onChange({ target: { name: field.name, value: e.target.checked } }, field)}
                disabled={field.disabled || (field.disableOnEdit && editMode)}
              />
            }
            label={field.label}
          />
        </Grid>
      );
    } else {
      const inputType = field.inputType || 'text';
      const shouldShrinkLabel = ['time', 'date', 'datetime-local'].includes(inputType);
      const validateError = field.validate && field.validate(formData[field.name], formData);
      const fieldError = fieldErrors[field.name] || validateError;
      return (
        <Grid item xs={12} sm={field.width || 6} key={field.name}>
          <TextField
            name={field.name}
            label={field.label}
            type={inputType}
            fullWidth
            value={formData[field.name] || ''}
            onChange={(e) => onChange(e, field)}
            required={typeof field.required === 'function' ? field.required(formData) : field.required}
            disabled={field.disabled || (field.disableOnEdit && editMode)}
            variant="outlined"
            size="small"
            multiline={field.multiline}
            rows={field.rows}
            helperText={fieldError || field.helperText}
            inputProps={field.inputProps}
            InputLabelProps={shouldShrinkLabel ? { shrink: true } : undefined}
            error={Boolean(fieldError)}
          />
        </Grid>
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'background.paper' }}>
        {title}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mt: 1, mb: 1 }}>
          {submitError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          ) : null}
          <Grid container spacing={3}>
            {fields.map((field) => renderField(field))}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="outlined">
          Cancel
        </Button>
        <Button onClick={onSubmit} color="primary" variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main Dynamic Container Component
const DynamicContainer = ({
  title,
  icon,
  columns,
  formFields,
  initialFormData = {},
  idField = 'id',
  breadcrumbPaths = [],
  rows = [],
  onCreate,
  onUpdate,
  onDelete,
  onRowDoubleClick,
  isLoading,
  isCdxUser = false, // Prefer functionCode + role rights; CDX still bypasses via hasFullAccess
  functionCode = null, // When set: Add→add, Edit/Delete→edit, like Access for menus/routes
  disableEdit = false, // Optional prop to disable edit button
  hideAdd = false, // Optional: hide Add button (e.g. for read-only lists like task queue)
  hideEdit = false, // Optional: hide Edit button
  hideDelete = false, // Optional: hide Delete button
  prepareEditData, // Optional function to prepare/edit data before showing in edit form
  onAddClick, // Optional custom handler for add button click
  onEditClick, // Optional custom handler for edit button click
  getDeleteBlockReason, // Optional async (row) => string; when set, Delete warns/blocks before the confirm dialog
  headerExtra, // Optional React node (e.g. icon button) rendered in the title bar before Add/Edit/Delete
  subHeader, // Optional React node below title bar (e.g. filter chips)
}) => {
  const { hasFullAccess, canAdd, canEdit, rightsLoaded } = useUserRights();

  // Same pattern as Access: CDX/full access bypass; otherwise require the matching right when functionCode is set.
  const rightsGate = Boolean(functionCode);
  const allowAdd = hasFullAccess || (rightsGate ? canAdd(functionCode) : isCdxUser);
  const allowEdit = hasFullAccess || (rightsGate ? canEdit(functionCode) : isCdxUser);
  const allowDelete = hasFullAccess || (rightsGate ? canEdit(functionCode) : isCdxUser);
  // Wait for rights load only for non-CDX (CDX never fetches rights — used to leave buttons stuck off)
  const waitingForRights = rightsGate && !rightsLoaded && !hasFullAccess;

  const [localRows, setLocalRows] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (Array.isArray(rows)) {
      setLocalRows(rows);
    }
  }, [rows]);

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setFormData(initialFormData);
  };

  const handleInputChange = (e, field) => {
    const { name, value } = e.target;
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
    setSubmitError('');
    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: value,
      };
      // Clear sub-category when category changes
      if (name === 'category' && field.onCategoryChange) {
        const updatedFormData = { ...newData, [name]: value };
        field.onCategoryChange(value, updatedFormData);
        newData.subCategory = '';
      }
      // Handle sourceConnector onChange if defined
      if (name === 'sourceConnector' && field.onChange) {
        const updatedFormData = { ...newData, [name]: value };
        field.onChange(value, updatedFormData);
      }
      return newData;
    });
  };

  const handleAdd = () => {
    if (!allowAdd) {
      toast.error('No Add permission for this function');
      return;
    }
    if (onAddClick) {
      onAddClick();
      return;
    }
    setEditMode(false);
    setFormData(initialFormData);
    handleOpenDialog();
  };

  const handleEdit = async () => {
    if (!allowEdit || disableEdit) {
      toast.error('No Edit permission for this function');
      return;
    }
    if (selectedRow) {
      const allRows = rows.length > 0 ? rows : localRows;
      const rowToEdit = allRows.find((row) => row.id === selectedRow);
      if (rowToEdit) {
        if (onEditClick) {
          onEditClick(rowToEdit);
          return;
        }
        setEditMode(true);

        // Use prepareEditData if provided, otherwise use default preparation
        let preparedData;
        if (prepareEditData) {
          try {
            // Handle both sync and async prepareEditData
            const result = prepareEditData(rowToEdit);
            preparedData = result instanceof Promise ? await result : result;
          } catch (error) {
            console.error('Error in prepareEditData:', error);
            toast.error(`Error preparing edit data: ${error.message}`);
            // Fallback to default preparation
            preparedData = {
              ...initialFormData,
              ...rowToEdit,
              sfl_mf_id: rowToEdit.sfl_mf_name,
              sfiTriggerType: {
                triggerType: rowToEdit.sfiTriggerType || 'Manual',
                intervalValue: rowToEdit.sfiInterval?.toString() || '',
                timeValue: rowToEdit.sfiTime || '',
              },
            };
          }
        } else {
          // Default preparation (backward compatibility)
          preparedData = {
            ...initialFormData,
            ...rowToEdit,
            sfl_mf_id: rowToEdit.sfl_mf_name,
            sfiTriggerType: {
              triggerType: rowToEdit.sfiTriggerType || 'Manual',
              intervalValue: rowToEdit.sfiInterval?.toString() || '',
              timeValue: rowToEdit.sfiTime || '',
            },
          };
        }

        setFormData(preparedData);
        handleOpenDialog();
      }
    }
  };

  const handleDeleteClick = async () => {
    if (!allowDelete) {
      toast.error('No Edit permission to delete');
      return;
    }
    if (selectedRow) {
      const allRows = rows.length > 0 ? rows : localRows;
      const rowToDelete = allRows.find((row) => row.id === selectedRow);
      const item = rowToDelete || selectedRow;
      if (getDeleteBlockReason) {
        try {
          const reason = await getDeleteBlockReason(item);
          if (reason) {
            toast.error(reason);
            return;
          }
        } catch (error) {
          console.error('Error checking whether delete is allowed:', error);
        }
      }
      setItemToDelete(item);
      setDeleteConfirmOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (itemToDelete) {
      if (onDelete) {
        try {
          // Pass the ID if itemToDelete is an object, otherwise pass as-is
          const deleteId = typeof itemToDelete === 'object' ? itemToDelete.id : itemToDelete;
          await onDelete(deleteId);
        } catch (error) {
          console.error('Error deleting:', error);
          toast.error(error.message || 'Error deleting');
        }
      } else {
        setLocalRows(localRows.filter((row) => row.id !== itemToDelete));
      }
      setSelectedRow(null);
      setItemToDelete(null);
    }
    setDeleteConfirmOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const collectFieldValidationErrors = () => {
    const errors = {};
    for (const field of formFields) {
      if (field.hideOnEdit && editMode) continue;
      if (field.hideOnCreate && !editMode) continue;
      if (field.disableOnEdit && editMode) continue;
      if (field.disabled) continue;
      if (field.show && !field.show(formData)) continue;

      const isRequired =
        typeof field.required === 'function' ? field.required(formData) : field.required;
      if (isRequired) {
        const val = formData[field.name];
        const empty =
          val == null ||
          (typeof val === 'string' && val.trim() === '') ||
          (typeof val === 'object' && !Array.isArray(val) && Object.keys(val).length === 0);
        if (empty) {
          errors[field.name] = `${field.label} is required`;
        }
      }

      if (field.validate) {
        const error = field.validate(
          field.name === 'sfiTriggerType' ? formData[field.name] : formData[field.name],
          formData
        );
        if (error) {
          errors[field.name] = typeof error === 'string' ? error : `${field.label} is invalid`;
        }
      }
    }
    return errors;
  };

  const handleSubmit = async () => {
    const clientErrors = collectFieldValidationErrors();
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      setSubmitError('Fix the highlighted fields and try again.');
      return;
    }

    setFieldErrors({});
    setSubmitError('');

    try {
      if (editMode) {
        if (onUpdate) {
          await onUpdate(selectedRow, formData);
        } else {
          setLocalRows(localRows.map((row) => (row.id === selectedRow ? { ...formData, id: selectedRow } : row)));
        }
      } else if (onCreate) {
        await onCreate(formData);
      } else {
        const newId = localRows.length > 0 ? Math.max(...localRows.map((row) => row.id)) + 1 : 1;
        const newRow = { ...formData, id: newId };
        if (idField in initialFormData) {
          newRow[idField] = newId.toString();
        }
        setLocalRows([...localRows, newRow]);
      }
      handleCloseDialog();
    } catch (error) {
      console.error(editMode ? 'Error updating:' : 'Error creating:', error);
      const message = error?.message || 'Save failed';
      const apiFieldErrors = parseFieldErrorsFromMessage(message);
      if (Object.keys(apiFieldErrors).length > 0) {
        setFieldErrors(apiFieldErrors);
        setSubmitError('Fix the highlighted fields and try again.');
      } else {
        setSubmitError(message);
      }
    }
  };

  const handleRowClick = (params) => {
    setSelectedRow(params.row.id);
  };

  // Helper to get selected row object
  const getSelectedRowObject = () => {
    if (!selectedRow) return null;
    const allRows = rows.length > 0 ? rows : localRows;
    return allRows.find((row) => row.id === selectedRow) || null;
  };

  const allPaths = breadcrumbPaths.length > 0 ? breadcrumbPaths : [{ label: title, icon: icon }];

  const Icon = icon || NetworkCheck;

  return (
    <RootDiv>
      <ContentDiv>
        <StyledPaper>
          <HeaderContainer>
            <TitleBar>
              <Title variant="h6">
                <Icon fontSize="small" />
                {title}
              </Title>
              <ButtonGroup>
                {headerExtra}
                {!hideAdd && (
                  <IconButton
                    color="primary"
                    onClick={handleAdd}
                    size="small"
                    disabled={!allowAdd || waitingForRights}
                    title={allowAdd ? 'Add' : 'No Add permission'}
                  >
                    <Add />
                  </IconButton>
                )}
                {!hideEdit && (
                  <IconButton
                    color="primary"
                    onClick={handleEdit}
                    disabled={!selectedRow || !allowEdit || disableEdit || waitingForRights}
                    size="small"
                    title={allowEdit ? 'Edit' : 'No Edit permission'}
                  >
                    <Edit />
                  </IconButton>
                )}
                {!hideDelete && (
                  <IconButton
                    color="error"
                    onClick={handleDeleteClick}
                    disabled={!selectedRow || !allowDelete || waitingForRights}
                    size="small"
                    title={allowDelete ? 'Delete' : 'No Edit permission to delete'}
                  >
                    <Delete />
                  </IconButton>
                )}
              </ButtonGroup>
            </TitleBar>
            {subHeader}
            {/* <DynamicBreadcrumbs paths={allPaths} /> */}
          </HeaderContainer>
          <DynamicDataGrid
            rows={rows.length > 0 ? rows : localRows}
            columns={columns}
            selectedRow={selectedRow}
            onRowClick={handleRowClick}
            onRowDoubleClick={onRowDoubleClick}
          />
        </StyledPaper>
      </ContentDiv>

      <DynamicFormDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title={editMode ? `Edit ${title}` : `Add ${title}`}
        formData={formData}
        onChange={handleInputChange}
        onSubmit={handleSubmit}
        fields={formFields}
        editMode={editMode}
        fieldErrors={fieldErrors}
        submitError={submitError}
      />

      <DeleteConfirmationDialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={`Delete ${title}`}
        message={`Are you sure you want to delete this ${title.toLowerCase()}? This action cannot be undone.`}
      />
    </RootDiv>
  );
};

export { DynamicContainer, DynamicDataGrid, DynamicFormDialog, DynamicBreadcrumbs };